import React, { memo } from 'react';
import { ConjuntoAgregado } from 'helper/windx';

interface LinhaConjuntoProps {
  conjunto: ConjuntoAgregado;
  selecionado: boolean;
  onToggle: (id: number) => void;
  altura: number;
  translateY: number;
}

function formatarNumero(valor: number, casas: number): string {
  return valor.toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
}

/**
 * Linha individual da tabela virtualizada de conjuntos. Mesma estrutura
 * de `LinhaUsina`: <label> envolvendo o checkbox para clicar em qualquer
 * celula propagar pro toggle. `React.memo` com comparacao por id +
 * selecionado + translateY.
 *
 * Adiciona classe `is-vazio` quando `qtd_usinas === 0` (latente — ver
 * comentario em `agregarConjuntos.ts`).
 */
function LinhaConjunto({
  conjunto,
  selecionado,
  onToggle,
  altura,
  translateY,
}: LinhaConjuntoProps) {
  const inputId = `windx-conjunto-${conjunto.id}`;
  const ehVazio = conjunto.qtd_usinas === 0;
  const classes = [
    'windx-seletor-conjuntos__linha',
    selecionado ? 'is-selecionado' : '',
    ehVazio ? 'is-vazio' : '',
  ].filter(Boolean).join(' ');

  return (
    <label
      htmlFor={inputId}
      aria-selected={selecionado}
      className={classes}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: `${altura}px`,
        transform: `translateY(${translateY}px)`,
      }}
    >
      <div role="cell" className="windx-seletor-conjuntos__cell windx-seletor-conjuntos__cell--check">
        <input
          id={inputId}
          type="checkbox"
          className="form-check-input"
          checked={selecionado}
          onChange={() => onToggle(conjunto.id)}
          aria-label={`Selecionar ${conjunto.label}`}
        />
      </div>
      <div role="cell" className="windx-seletor-conjuntos__cell windx-seletor-conjuntos__cell--codigo">
        {conjunto.cod_conjunto}
      </div>
      <div role="cell" className="windx-seletor-conjuntos__cell windx-seletor-conjuntos__cell--nome">
        {conjunto.ponto_de_conexao}
      </div>
      <div
        role="cell"
        className="windx-seletor-conjuntos__cell windx-seletor-conjuntos__cell--label"
        style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.85em' }}
        title={`Identificador unico: ${conjunto.label}`}
      >
        {conjunto.label}
      </div>
      <div role="cell" className="windx-seletor-conjuntos__cell windx-seletor-conjuntos__cell--uf">
        {conjunto.uf_sigla ?? '—'}
      </div>
      <div role="cell" className="windx-seletor-conjuntos__cell windx-seletor-conjuntos__cell--sub-sm">
        {conjunto.subsistema_sm_sigla ?? '—'}
      </div>
      <div role="cell" className="windx-seletor-conjuntos__cell windx-seletor-conjuntos__cell--sub-sh">
        {conjunto.subsistema_sh_sigla ?? '—'}
      </div>
      <div
        role="cell"
        className="windx-seletor-conjuntos__cell windx-seletor-conjuntos__cell--epe"
        title={conjunto.localizacao_epe ?? undefined}
      >
        {conjunto.localizacao_epe ?? '—'}
      </div>
      <div role="cell" className="windx-seletor-conjuntos__cell windx-seletor-conjuntos__cell--qtd">
        {formatarNumero(conjunto.qtd_usinas, 0)}
      </div>
      <div role="cell" className="windx-seletor-conjuntos__cell windx-seletor-conjuntos__cell--mw">
        {formatarNumero(conjunto.potencia_total_mw, 2)}
      </div>
    </label>
  );
}

export default memo(LinhaConjunto, (prev, next) => (
  prev.conjunto.id === next.conjunto.id
  && prev.selecionado === next.selecionado
  && prev.altura === next.altura
  && prev.translateY === next.translateY
));
